// src/js/main.js
import Alpine from 'alpinejs';
import '../css/style.css';

// Make Alpine available globally
window.Alpine = Alpine;

// Define the domain portfolio component
document.addEventListener('alpine:init', () => {
  Alpine.data('domainPortfolio', () => ({
    domains: [],
    loading: true,
    filter: '',
    sortBy: 'name',
    selectedDomain: null,
    showToast: false,
    offerForm: {
      name: '',
      email: '',
      price: '',
      message: ''
    },
    
    // Initialize the component
    init() {
      this.fetchDomains();
    },
    
    // Fetch domains from the API
    async fetchDomains() {
      try {
        // In development, we might want to use mock data
        if (import.meta.env.DEV) {
          // Simulate API delay
          await new Promise(resolve => setTimeout(resolve, 800));
          this.domains = this.getSampleDomains();
        } else {
          // In production, use the real API
          const response = await fetch('/api/domains');
          if (!response.ok) {
            throw new Error('Failed to fetch domains');
          }
          this.domains = await response.json();
        }
      } catch (error) {
        console.error('Error fetching domains:', error);
        // Fallback to sample data if API fails
        this.domains = this.getSampleDomains();
      } finally {
        this.loading = false;
      }
    },
    
    // Format date for display
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }).format(date);
    },
    
    // Get TLD from domain name
    getTld(domainName) {
      const parts = domainName.split('.');
      return parts.length > 1 ? `.${parts[parts.length - 1]}` : '';
    },
    
    // Select a domain to make an offer
    selectDomain(domain) {
      this.selectedDomain = domain;
      // Reset form
      this.offerForm = {
        name: '',
        email: '',
        price: '',
        message: ''
      };
    },
    
    // Submit an offer for a domain
    submitOffer() {
      // In a real app, you would send this to your backend
      console.log('Offer submitted:', {
        domain: this.selectedDomain.name,
        ...this.offerForm
      });
      
      // Close the modal
      this.selectedDomain = null;
      
      // Show success toast
      this.showToast = true;
      setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },
    
    // Computed property for filtered and sorted domains
    get filteredDomains() {
      return this.domains
        .filter(domain => domain.name.toLowerCase().includes(this.filter.toLowerCase()))
        .sort((a, b) => {
          if (this.sortBy === 'name') {
            return a.name.localeCompare(b.name);
          } else if (this.sortBy === 'created') {
            return new Date(b.createdOn) - new Date(a.createdOn);
          } else if (this.sortBy === 'tld') {
            const tldA = this.getTld(a.name);
            const tldB = this.getTld(b.name);
            return tldA.localeCompare(tldB);
          }
          return 0;
        });
    },
    
    // Sample data for development/testing
    getSampleDomains() {
      return [
        { 
          id: '1', 
          name: 'example.com', 
          status: 'active', 
          createdOn: '2023-01-15',
          nameservers: ['ns1.cloudflare.com', 'ns2.cloudflare.com']
        },
        { 
          id: '2', 
          name: 'domain-market.io', 
          status: 'active', 
          createdOn: '2023-03-22',
          nameservers: ['ns1.cloudflare.com', 'ns2.cloudflare.com']
        },
        { 
          id: '3', 
          name: 'best-domains.net', 
          status: 'active', 
          createdOn: '2023-05-10',
          nameservers: ['ns1.cloudflare.com', 'ns2.cloudflare.com']
        },
        { 
          id: '4', 
          name: 'premium-names.com', 
          status: 'active', 
          createdOn: '2023-02-28',
          nameservers: ['ns1.cloudflare.com', 'ns2.cloudflare.com']
        },
        { 
          id: '5', 
          name: 'domain-investor.com', 
          status: 'active', 
          createdOn: '2023-04-17',
          nameservers: ['ns1.cloudflare.com', 'ns2.cloudflare.com']
        },
        { 
          id: '6', 
          name: 'buy-domains-here.com', 
          status: 'active', 
          createdOn: '2023-06-05',
          nameservers: ['ns1.cloudflare.com', 'ns2.cloudflare.com']
        },
        { 
          id: '7', 
          name: 'tech-domains.dev', 
          status: 'active', 
          createdOn: '2023-07-12',
          nameservers: ['ns1.cloudflare.com', 'ns2.cloudflare.com']
        },
        { 
          id: '8', 
          name: 'startup-name.co', 
          status: 'active', 
          createdOn: '2023-08-18',
          nameservers: ['ns1.cloudflare.com', 'ns2.cloudflare.com']
        },
        { 
          id: '9', 
          name: 'brandable.io', 
          status: 'active', 
          createdOn: '2023-09-21',
          nameservers: ['ns1.cloudflare.com', 'ns2.cloudflare.com']
        }
      ];
    }
  }));
});

Alpine.start();